import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="forgot-password-page">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <Link
            to="/login"
            data-testid="forgot-back-to-login"
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700 mb-6"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>

          <div className="mb-8 flex flex-col items-center text-center">
            <div className="mb-6 flex flex-col items-center">
              <img
                src="/assets/civilx-tms-logo.png"
                alt="CivilX TMS"
                className="h-16 w-auto object-contain"
                data-testid="forgot-logo"
              />
              <p className="text-[10px] text-slate-500 uppercase tracking-[0.25em] font-semibold mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Dispatch · Track · Deliver</p>
            </div>
            <h1
              className="text-3xl font-bold tracking-tight text-slate-900 mb-2"
              style={{ fontFamily: 'Work Sans, sans-serif' }}
            >
              Reset your password
            </h1>
            <p
              className="text-sm text-slate-600"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              Enter the email tied to your CivilX TMS account and we&rsquo;ll send you a secure link to set a new password.
            </p>
          </div>

          {submitted ? (
            <div
              data-testid="forgot-success-message"
              className="p-4 bg-emerald-50 border border-emerald-200 rounded-md"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-emerald-900">Check your inbox</p>
                  <p className="text-xs text-emerald-800 mt-1">
                    If <strong>{email}</strong> matches an account, we just sent a reset link.
                    It will expire in 1 hour. Don't see it? Check your spam folder or try again.
                  </p>
                </div>
              </div>
              <button
                data-testid="forgot-resubmit-button"
                onClick={() => { setSubmitted(false); setError(''); }}
                className="mt-4 text-xs font-semibold text-[#002FA7] hover:text-[#00247D] hover:underline"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-form">
              {error && (
                <div
                  data-testid="forgot-error-message"
                  className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600"
                >
                  {error}
                </div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    id="email"
                    type="email"
                    required
                    data-testid="forgot-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none transition-all"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !email}
                data-testid="forgot-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#002FA7] text-white py-3 px-4 rounded-md font-semibold hover:bg-[#00247D] focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-2 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          )}
        </div>
      </div>

      <div
        className="hidden lg:block lg:w-1/2 relative"
        style={{
          backgroundImage: 'url(/images/login-hero.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-900/40 to-transparent" />
      </div>
    </div>
  );
};

export default ForgotPassword;
